import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { MessageSquare, Sparkles, Rocket, ArrowRight } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const steps = [
  {
    number: "1",
    icon: MessageSquare,
    title: "5 questions",
    subtitle: "60 secondes",
    description: "Situation, revenus, logement... c'est tout."
  },
  {
    number: "2",
    icon: Sparkles,
    title: "Résultat instantané",
    subtitle: "En direct",
    description: "Découvre combien tu peux récupérer."
  },
  {
    number: "3",
    icon: Rocket,
    title: "Skipya gère",
    subtitle: "Automatique",
    description: "Rappels, démarches, suivi. Tu respires."
  }
];

export const HowItWorksSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { isDark } = useTheme();

  return (
    <section 
      id="how-it-works"
      className={`section-apple px-6 ${isDark ? 'bg-black' : 'bg-[#f5f5f7]'}`}
      data-testid="how-it-works-section"
    >
      <div className="max-w-[980px] mx-auto">
        {/* Headline */}
        <motion.div
          ref={ref}
          className="text-center mb-16"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <p className={`text-[13px] tracking-[0.02em] uppercase mb-4 ${isDark ? 'text-[#81d8d0]' : 'text-[#81d8d0]'}`}>
            Simple comme bonjour
          </p>
          <h2 className={`text-[40px] sm:text-[48px] md:text-[56px] font-semibold tracking-[-0.03em] leading-[1.1] ${
            isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'
          }`}>
            Tu réponds.<br />
            <span className="text-[#81d8d0]">On s'occupe du reste.</span>
          </h2>
        </motion.div>

        {/* Steps - Connected flow */}
        <div className="relative">
          {/* Connection line - Desktop only */}
          <div className="hidden md:block absolute top-[60px] left-[16.66%] right-[16.66%] h-[2px]">
            <motion.div 
              className="h-full bg-gradient-to-r from-[#81d8d0]/20 via-[#81d8d0] to-[#81d8d0]/20"
              initial={{ scaleX: 0 }}
              animate={isInView ? { scaleX: 1 } : {}}
              transition={{ duration: 1, delay: 0.5 }}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                className="relative text-center"
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 + index * 0.2 }}
              >
                {/* Step circle */}
                <div className="relative inline-flex flex-col items-center">
                  <div className={`w-[120px] h-[120px] rounded-full flex flex-col items-center justify-center mb-6 ${
                    isDark 
                      ? 'bg-[#141414] border border-[#3a3a3c]' 
                      : 'bg-white border border-[#d2d2d7] shadow-sm'
                  }`}>
                    <step.icon 
                      size={32} 
                      strokeWidth={1} 
                      className="text-[#81d8d0] mb-2"
                    />
                    <span className={`text-[11px] font-medium ${isDark ? 'text-[#6e6e73]' : 'text-[#86868b]'}`}>
                      {step.subtitle}
                    </span>
                  </div>
                  
                  {/* Number badge */}
                  <div className={`absolute -top-1 -right-1 w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-semibold ${
                    isDark 
                      ? 'bg-[#81d8d0] text-[#000]' 
                      : 'bg-[#81d8d0] text-[#000]'
                  }`}>
                    {step.number}
                  </div>
                </div>
                
                {/* Title */}
                <h3 className={`text-[19px] font-semibold tracking-[-0.01em] mb-2 ${
                  isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'
                }`}>
                  {step.title}
                </h3>
                
                {/* Description */}
                <p className={`text-[15px] tracking-[-0.01em] max-w-[240px] mx-auto ${
                  isDark ? 'text-[#86868b]' : 'text-[#6e6e73]'
                }`}>
                  {step.description}
                </p>

                {/* Arrow between steps - Mobile only */}
                {index < steps.length - 1 && (
                  <div className="md:hidden flex justify-center my-4">
                    <ArrowRight size={20} className={`rotate-90 ${isDark ? 'text-[#3a3a3c]' : 'text-[#d2d2d7]'}`} />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom emphasis */}
        <motion.p
          className={`text-center mt-16 text-[15px] ${isDark ? 'text-[#6e6e73]' : 'text-[#86868b]'}`}
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 1 }}
        >
          Pas de formulaire Cerfa. Pas de jargon. <span className={`font-medium ${isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'}`}>Juste des résultats.</span>
        </motion.p>
      </div>
    </section>
  );
};

export default HowItWorksSection;
